import { useCallback, useState } from "react";

import { NotificationSeverity, NotificationSnackbarProps } from "./types";

const DEFAULT_AUTO_HIDE_DURATION = 2000;

export interface UseNotificationSnackbarReturn {
    showSuccess: (title: string, description?: string) => void;
    // error принимает пойманное исключение: текст ошибки достаём сами, чтобы каждая фича
    // не повторяла `e instanceof Error ? e.message : undefined`.
    showError: (title: string, error?: unknown) => void;
    showWarning: (title: string, description?: string) => void;
    showInfo: (title: string, description?: string) => void;
    close: () => void;
    snackbarProps: NotificationSnackbarProps;
}

/*
* Уведомления фичи без стора: состояние живёт в самой фиче, компонент рендерится её корнем.
* Тост живёт вместе с фичей: если после действия происходит переход на другую страницу,
* показывать сообщение нужно уже на странице назначения.
* */
export const useNotificationSnackbar = (
    autoHideDuration: number | null = DEFAULT_AUTO_HIDE_DURATION
): UseNotificationSnackbarReturn => {
    const [open, setOpen] = useState<boolean>(false);
    const [title, setTitle] = useState<string>("");
    const [description, setDescription] = useState<string | undefined>(undefined);
    const [severity, setSeverity] = useState<NotificationSeverity>("info");
    const [toastKey, setToastKey] = useState<number>(0);

    const show = useCallback(
        (nextSeverity: NotificationSeverity, nextTitle: string, nextDescription?: string) => {
            setSeverity(nextSeverity);
            setTitle(nextTitle);
            setDescription(nextDescription);
            setToastKey((key) => key + 1);
            setOpen(true);
        },
        []
    );

    const showSuccess = useCallback(
        (nextTitle: string, nextDescription?: string) =>
            show("success", nextTitle, nextDescription),
        [show]
    );

    const showError = useCallback(
        (nextTitle: string, error?: unknown) => {
            if (error !== undefined) console.error(nextTitle, error);
            show("error", nextTitle, error instanceof Error ? error.message : undefined);
        },
        [show]
    );

    const showWarning = useCallback(
        (nextTitle: string, nextDescription?: string) =>
            show("warning", nextTitle, nextDescription),
        [show]
    );

    const showInfo = useCallback(
        (nextTitle: string, nextDescription?: string) => show("info", nextTitle, nextDescription),
        [show]
    );

    const close = useCallback(() => setOpen(false), []);

    return {
        showSuccess,
        showError,
        showWarning,
        showInfo,
        close,
        snackbarProps: {
            open,
            title,
            description,
            severity,
            onClose: close,
            autoHideDuration,
            toastKey,
        },
    };
};
